<template>
  <div class="login">
    <div>
      <v-container class="bg_body" fluid v-if="!smsLogsVisible && !businessLogicVisible && !appVersionsVisible && !tenantsVisible && !invitedUsersAllVisible && !signupWaitlistVisible && !marketsVisible && !onboardingVisible && !marketingConsoleVisible" v-show="!modal">
        <v-row align="center" justify="center">
          <v-col cols="12">
            <ValidationObserver ref="validator" v-slot="{ }">
              <div class="lc" :class="{ 'lc--loading': status === 'loading', 'lc--otp': step === 2 }">
                <v-form v-on:submit.prevent v-on:submit="submit">

                  <!-- Step 1: Phone -->
                  <template v-if="step === 1">
                    <h1 class="lc__title">Sign in to Kiey</h1>
                    <p class="lc__subtitle">Enter your phone number to receive a secure login code.</p>

                    <div class="lc__field">
                      <ValidationProvider ref="phone_number" mode="passive" rules="required|size:10" v-slot="{ errors }">
                        <input
                          v-model="phoneNumberInput"
                          type="tel"
                          inputmode="tel"
                          autocomplete="tel-national"
                          name="tel"
                          placeholder="XXX-XXX-XXXX"
                          class="lc__input"
                          :class="{ 'lc__input--error': errors.length }"
                        />
                        <span v-if="errors.length" class="lc__error">{{ errors[0] }}</span>
                      </ValidationProvider>
                    </div>

                    <div class="lc__links">
                      <a href="#" @click.prevent="showModal" class="lc__link">Need help? Contact us</a>
                      <span class="lc__dot">&middot;</span>
                      <span @click="signUpWizardVisible = true" class="lc__link lc__link--blue" style="cursor: pointer;">Sign up</span>
                    </div>

                    <p class="lc__helper">We'll send a one-time SMS code to verify your number.</p>

                    <button type="button" class="lc__cta" :disabled="status === 'loading'" @click="verify">
                      <v-progress-circular v-if="status === 'loading'" indeterminate size="20" width="2" color="white" style="margin-right: 8px;"></v-progress-circular>
                      Continue
                    </button>

                    <p class="lc__trust">Secure login via SMS verification</p>
                  </template>

                  <!-- Step 2: OTP -->
                  <template v-else>
                    <button type="button" class="lc__back" @click="step = 1">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      Back
                    </button>
                    <h1 class="lc__title">Enter your code</h1>
                    <p class="lc__subtitle">We sent a 6-digit code to <strong>{{ phone_number }}</strong></p>
                    <p class="lc__change">Wrong number? <a href="#" @click.prevent="step = 1" class="lc__link--blue">Change</a></p>

                    <!-- Keep original phone validator hidden for submit logic -->
                    <ValidationProvider ref="phone_number" mode="passive" rules="required|size:10" v-slot="{ errors }" style="display:none;">
                      <input v-model="phone_number" v-mask="mask" type="hidden" />
                      <span v-if="errors.length"></span>
                    </ValidationProvider>

                    <div class="lc__otp">
                      <ValidationProvider ref="code" mode="passive" v-slot="{ errors }" rules="required|code:6">
                        <OPTInput :onChangeOTP="onChangeOTP" :numberOfInput="6" />
                        <span v-if="errors.length" class="lc__error" style="text-align:center; display:block; margin-top:6px;">{{ errors[0] }}</span>
                      </ValidationProvider>
                    </div>

                    <div class="lc__resend">
                      <span v-if="!resent">Didn't get a code? <a href="#" @click.prevent="resendSMS" class="lc__link--blue">Resend</a></span>
                      <span v-else-if="timerStart > 0" style="color:#6B7280;">Code resent ({{ timerStart }}s)</span>
                      <span v-else style="color:#6B7280;">Code resent</span>
                    </div>

                    <button type="button" class="lc__cta" :disabled="phone != phone_number || status === 'loading' || loading" @click="submit">
                      <v-progress-circular v-if="status === 'loading' || loading" indeterminate size="20" width="2" color="white" style="margin-right: 8px;"></v-progress-circular>
                      Log in
                    </button>

                    <p class="lc__trust">Secure login via SMS verification</p>
                  </template>

                </v-form>
              </div>
            </ValidationObserver>
          </v-col>
        </v-row>
      </v-container>
    </div>
    <div ref="smsLogsContainer" v-if="smsLogsVisible" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; background: #f7fafc; overflow-y: auto;">
      <div style="padding: 8px 24px 0;">
        <v-btn text small @click="hideSuperUserView('smsLogsVisible')">
          &larr; Back to Company List
        </v-btn>
      </div>
      <sms-logs-view />
    </div>
    <div ref="appVersionsContainer" v-if="appVersionsVisible" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; background: #f5f6f8; overflow-y: auto;">
      <div style="padding: 8px 24px 0;">
        <v-btn text small @click="hideSuperUserView('appVersionsVisible')">
          &larr; Back to Company List
        </v-btn>
      </div>
      <app-versions-view />
    </div>
    <div ref="invitedUsersAllContainer" v-if="invitedUsersAllVisible" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; background: #f7fafc; overflow-y: auto;">
      <div style="padding: 8px 24px 0;">
        <v-btn text small @click="hideSuperUserView('invitedUsersAllVisible')">
          &larr; Back to Company List
        </v-btn>
      </div>
      <invited-users-all-view />
    </div>
    <div ref="tenantsContainer" v-if="tenantsVisible" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; background: #f7fafc; overflow-y: auto;">
      <div style="padding: 8px 24px 0;">
        <v-btn text small @click="hideSuperUserView('tenantsVisible')">
          &larr; Back to Company List
        </v-btn>
      </div>
      <tenants-view />
    </div>
    <div ref="signupWaitlistContainer" v-if="signupWaitlistVisible" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; background: #f7fafc; overflow-y: auto;">
      <div style="padding: 8px 24px 0;">
        <v-btn text small @click="hideSuperUserView('signupWaitlistVisible')">
          &larr; Back to Company List
        </v-btn>
      </div>
      <signup-waitlist-view />
    </div>
    <div ref="marketsContainer" v-if="marketsVisible" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; background: #f7fafc; overflow-y: auto;">
      <div style="padding: 8px 24px 0;">
        <v-btn text small @click="hideSuperUserView('marketsVisible')">
          &larr; Back to Company List
        </v-btn>
      </div>
      <markets-view />
    </div>
    <div ref="marketingConsoleContainer" v-if="marketingConsoleVisible" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; background: #f7fafc; overflow-y: auto;">
      <div style="padding: 8px 24px 0;">
        <v-btn text small @click="hideSuperUserView('marketingConsoleVisible')">
          &larr; Back to Company List
        </v-btn>
      </div>
      <marketing-console-view />
    </div>
    <div ref="onboardingContainer" v-if="onboardingVisible" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; background: #f7fafc; overflow-y: auto;">
      <div style="padding: 8px 24px 0;">
        <v-btn text small @click="hideSuperUserView('onboardingVisible')">
          &larr; Back to Company List
        </v-btn>
      </div>
      <onboarding-view />
    </div>
    <div ref="businessLogicContainer" v-if="businessLogicVisible" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; background: #f5f6f8; display: flex; flex-direction: column;">
      <div style="padding: 8px 24px 0;">
        <v-btn text small @click="hideBusinessLogic">
          &larr; Back to Company List
        </v-btn>
      </div>
      <div style="flex: 1; overflow-y: auto;">
        <business-logic-view />
      </div>
      <div style="padding: 4px 40px; display: flex; align-items: center; justify-content: space-between; white-space: nowrap; background: #f5f6f8;">
        <div style="display: flex; gap: 16px;">
          <span style="font-size: 10px; font-weight: 400; color: #8a8f98; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">Facebook</span>
          <span style="font-size: 10px; font-weight: 400; color: #8a8f98; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">Twitter</span>
          <span style="font-size: 10px; font-weight: 400; color: #8a8f98; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">Instagram</span>
        </div>
        <div style="display: flex; gap: 16px;">
          <span style="font-size: 10px; font-weight: 400; color: #8a8f98; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">@ KIEY 2026</span>
          <span style="font-size: 10px; font-weight: 400; color: #8a8f98; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">Terms of Use</span>
          <span style="font-size: 10px; font-weight: 400; color: #8a8f98; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">Privacy Policy</span>
          <span style="font-size: 10px; font-weight: 400; color: #8a8f98; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">Licenses</span>
        </div>
      </div>
    </div>
    <list-modal
        ref="tenantModal"
        :show="modal"
        v-if="modal"
        :onInactive="onInactive"
        :onError="onError"
        @show-sms-logs="showSmsLogsView"
        @show-business-logic="showBusinessLogicView"
        @show-app-versions="showAppVersionsView"
        @show-invited-users-all="showInvitedUsersAllView"
        @show-tenants="showTenantsView"
        @show-signup-waitlist="showSignupWaitlistView"
        @show-markets="showMarketsView"
        @show-marketing-console="showMarketingConsoleView"
        @show-onboarding="showOnboardingView"
    />
    <contact-us-modal
        :show="contactModal"
        :hide="closeModal"
        v-if="contactModal"
    />
    <ChangeRoleModal
        :title="showChangeRoleModalTitle"
        :show="showChangeRoleModal"
        :hide="closeChangeRoleModal"
        :confirm="confirmEraseAccount"
        :state="deleting_status"
    />
    <RoleChangedModal
        :show="showRoleChangedModal"
        :hide="closeRoleChangedModal"
    />
    <sign-up-wizard
        :show.sync="signUpWizardVisible"
        v-if="signUpWizardVisible"
    />

  </div>
</template>

<script>
  import {ValidationProvider, ValidationObserver} from "vee-validate";
  import OPTInput from "../../assets/js/vue-otp-input/OTPInput";
  import {mask} from "vue-the-mask";
  import {mapState} from 'vuex';
  // import initCaptcha from "@/config/captcha";
  import listModal from "./modals/list";
  import contactUs from "./modals/contact_us";
  import AccountMixin from "@/mixins/account";
  import SmsLogsView from "@/views/owner/sms_logs/list.vue";
  import BusinessLogicView from "@/views/business-logic/index.vue";
  import AppVersionsView from "@/views/owner/app_version_management/index.vue";
  import InvitedUsersAllView from "@/views/owner/invited_users/all.vue";
  import TenantsView from "@/views/owner/tenants/list.vue";
  import SignupWaitlistView from "@/views/owner/signup_waitlist/index.vue";
  import MarketsView from "@/views/owner/markets/index.vue";
  import MarketingConsoleView from "@/views/owner/marketing/super_console.vue";
  import DeploymentView from "@/views/owner/deployment/index.vue";
  import SignUpWizard from "./SignUpWizard.vue";

  export default {
    name: "Login",
    mixins: [AccountMixin],
    created() {
      if (!window.confirmationResult) {
        this.step = 1;
      } else {
        this.step = 2;
      }
    },
    mounted() {
      // const { auth, recaptcha } = initCaptcha();
      this.phone_number = "";
      this.step = 1;
      if (this.$route.query.is_admin) {
        this.phone_number = this.$route.query.phone_number;
        this.$refs.phone_number.setErrors(["You do not have access to KIEY on the web. Please download the app to sign in."]);
      }
      const signupParam = this.$route.query.signup;
      if (signupParam === 'true' || signupParam === 'client' || signupParam === 'vendor' || signupParam === 'agent') {
        this.signUpWizardVisible = true;
      }
      document.documentElement.style.overflow = 'hidden';

      // Returning from an aliased tenant back to the super_user home. Restore
      // the super_user's saved auth + options, then open the tenant-selection
      // modal.
      if (localStorage.getItem('return_to_super_user') === 'true') {
        localStorage.removeItem('return_to_super_user');
        this.restoreSuperUserSession();
      }
    },
    directives: {
      mask
    },
    components: {
      OPTInput,
      ValidationProvider,
      ValidationObserver,
      "list-modal": listModal,
      "contact-us-modal": contactUs,
      "sms-logs-view": SmsLogsView,
      "business-logic-view": BusinessLogicView,
      "app-versions-view": AppVersionsView,
      "invited-users-all-view": InvitedUsersAllView,
      "tenants-view": TenantsView,
      "signup-waitlist-view": SignupWaitlistView,
      "markets-view": MarketsView,
      "marketing-console-view": MarketingConsoleView,
      "onboarding-view": DeploymentView,
      "sign-up-wizard": SignUpWizard,
    },
    computed: {
      ...mapState({
        status: state => state.Authenticate.status,
        phone: state => state.Authenticate.phone_number,
        modal: state => state.Authenticate.modal,
      }),
      phoneNumberInput: {
        get() {
          return this.phone_number;
        },
        set(value) {
          let digits = String(value == null ? '' : value).replace(/\D/g, '');
          if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
          if (digits.length > 10) digits = digits.slice(-10);
          let formatted = digits;
          if (digits.length > 6) formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
          else if (digits.length > 3) formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
          this.phone_number = formatted;
        },
      },
    },
    data: () => ({
      signUpWizardVisible: false,
      contactModal: false,
      smsLogsVisible: false,
      businessLogicVisible: false,
      appVersionsVisible: false,
      invitedUsersAllVisible: false,
      tenantsVisible: false,
      signupWaitlistVisible: false,
      marketsVisible: false,
      marketingConsoleVisible: false,
      onboardingVisible: false,
      step: 1,
      phone_number: "",
      code: '',
      mask: "###-###-####",
      resent: false,
      countdown: false,
      timerStart: 0,
      loading: false
    }),
    watch: {
      resent(newVal) {
        const self = this;
        if (newVal) { // start countdown
          if (this.countdown) {
            clearInterval(this.countdown);
          }
          this.timerStart = 60;
          this.countdown = setInterval(function () {
            self.timerStart -= 1;
            if (self.timerStart === 0) {
              self.resent = false;
              clearInterval(self.countdown);
            }
          }, 1000);
        }
      }
    },
    beforeDestroy() {
      if (this.countdown) {
        clearInterval(this.countdown);
      }
      document.documentElement.style.overflow = '';
    },
    methods: {
      restoreSuperUserSession() {
        let backup = null;
        try {
          const raw = localStorage.getItem('super_user_return');
          if (raw) backup = JSON.parse(raw);
        } catch (e) {
          console.error('[restoreSuperUserSession] Failed to parse backup:', e);
        }
        if (!backup || !backup.options || !backup.options.is_system_super_user) {
          console.warn('[restoreSuperUserSession] No usable options backup; staying on login');
          return;
        }

        // Restore the super_user's tenant options and open the list-modal.
        // The super_user's Firebase session is still live, so when they pick a
        // tenant, activate_ut can re-authenticate without re-prompting OTP.
        this.$store.commit('Authenticate/set_options', backup.options);
        localStorage.removeItem('super_user_return');
        this.$store.commit('Authenticate/state_modal', true);
      },
      showModal() {
        this.contactModal = true;
      },
      closeModal() {
        this.contactModal = false;
      },
      onChangeOTP(otp) {
        if (otp === "Enter") {
          this.submit();
          return true;
        }
        if (otp) {
          this.code = otp.toString();
          this.$refs.code.syncValue(otp);
        } else {
          this.code = '';
          this.$refs.code.syncValue('');
        }
      },
      async resendSMS() {
        const validator = await this.$refs.phone_number.validate();

        if (!validator.valid) return;

        try {
          await this.$store.dispatch('Authenticate/send_sms', this.phone_number);
          this.resent = true;
        } catch (error) {
          this.$refs.phone_number.setErrors([
            error?.message || "Error sending SMS"
          ]);
        }
      },
      async submit() {
        const validator = await this.$refs.validator.validate();
        const self = this;

        if (validator) {
          try {
            const user = await this.$store.dispatch('Authenticate/verify_sms_code', self.code);
            self.loading = true;

            // Después de Firebase Auth, validar tenants/billing/admin via API
            const isClient = await this.$store.dispatch('Authenticate/checkIfClient', {
              phone_number: self.phone_number
            });

            if (isClient === "NO_ADMIN") {
              self.loading = false;
              self.$refs.phone_number.setErrors(["You do not have access to KIEY on the web. Please download the app to sign in."]);
              self.step = 1;
              return;
            } else if (isClient === "NO_ACTIVE") {
              self.loading = false;
              self.$refs.phone_number.setErrors(["You need to complete your sign-up first in order to login. Check your email inbox or sms inbox."]);
              self.step = 1;
              return;
            } else if (isClient === "NO_BILLING") {
              self.loading = false;
              self.$refs.phone_number.setErrors(["Your account is inactive."]);
              self.step = 1;
              return;
            }

            // Si pasa validación, proceder con login
            try {
              await self.$store.dispatch('Authenticate/activate', {
                phone_number: self.phone_number,
                auth_id: user.uid,
              });
              self.step = 1;
              self.phone_number = "";
              self.loading = false;
            } catch (error) {
              self.loading = false;
              if (error.code === 'NOT_REGISTERED') {
                self.$router.push('/owner/sign_up');
              } else {
                self.$refs.phone_number.setErrors([error]);
              }
            }
          } catch (error) {
            self.loading = false;
            self.$refs.code.setErrors([error]);
            self.$store.commit('Authenticate/status', '');
          }
        }
      },
      onError(error) {
        const self = this;
        self.$refs.phone_number.setErrors([error]);
      },
      async verify() {
        const validator = await this.$refs.validator.validate();
        const self = this;

        if (validator) {
          // Paso 1: send_sms valida existencia via API y envía SMS
          this.$store
            .dispatch("Authenticate/send_sms", this.phone_number)
            .then(function () {
              self.step = 2;
            })
            .catch(function (error) {
              console.error('[verify] send_sms error:', error);
              const msg = error?.response?.data?.message || error?.message || 'Error sending SMS';
              self.$refs.phone_number.setErrors([msg]);
            });
        }
      },
      showSuperUserView() {
        const container = document.querySelector('.module-container.custom-top');
        if (container) container.style.transform = 'none';
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      },
      hideSuperUserView(viewName) {
        this[viewName] = false;
        const container = document.querySelector('.module-container.custom-top');
        if (container) container.style.transform = '';
        document.documentElement.style.overflow = 'hidden';
        document.querySelectorAll('.v-dialog__content, .v-overlay').forEach(el => {
          el.style.display = '';
        });
        // Re-open the tenant list modal that was hidden when this overlay opened,
        // so "Back to Company List" actually returns to the company list instead of
        // dropping the super user onto the bare login page.
        this.$store.commit('Authenticate/state_modal', true);
      },
      showSmsLogsView() {
        this.smsLogsVisible = true;
        this.$nextTick(() => this.showSuperUserView());
      },
      showAppVersionsView() {
        this.appVersionsVisible = true;
        this.$nextTick(() => this.showSuperUserView());
      },
      showInvitedUsersAllView() {
        this.invitedUsersAllVisible = true;
        this.$nextTick(() => this.showSuperUserView());
      },
      showTenantsView() {
        this.tenantsVisible = true;
        this.$nextTick(() => this.showSuperUserView());
      },
      showSignupWaitlistView() {
        this.signupWaitlistVisible = true;
        this.$nextTick(() => this.showSuperUserView());
      },
      showMarketsView() {
        this.marketsVisible = true;
        this.$nextTick(() => this.showSuperUserView());
      },
      showMarketingConsoleView() {
        this.marketingConsoleVisible = true;
        this.$nextTick(() => this.showSuperUserView());
      },
      showOnboardingView() {
        this.onboardingVisible = true;
        this.$nextTick(() => this.showSuperUserView());
      },
      showBusinessLogicView() {
        this.businessLogicVisible = true;
        this.$nextTick(() => this.showSuperUserView());
        document.querySelectorAll('.footer-container, .app-footer, .v-footer').forEach(el => el.style.display = 'none');
      },
      hideBusinessLogic() {
        this.hideSuperUserView('businessLogicVisible');
        document.querySelectorAll('.footer-container, .app-footer, .v-footer').forEach(el => el.style.display = '');
      },
      confirmAccount(tenant) {
        // TODO when old account was been erased
        this.$refs.tenantModal.onSelectTenant(tenant);
      }
    }
  }
  ;
</script>
<style>
  /** custom input */
  .otp-input {
    margin: 4px 10px 0 2px;
    font-size: 18px;
    text-align: center;
    width: 29px !important;
  }

  .otp-input.error {
    border: 1px solid red !important;
  }

  .otp-input::-webkit-inner-spin-button,
  .otp-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* OTP boxes — override Vuetify inside OPTInput (52x52, 12px radius, 2px #b4c0d5) */
  .lc__otp .opt-input {
    width: 52px !important;
    min-width: 52px !important;
    height: 52px !important;
    margin: 0 5px !important;
    padding: 0 !important;
    border-radius: 12px !important;
    flex: 0 0 auto !important;
  }

  .lc__otp .opt-input .v-input__slot {
    border: 2px solid #b4c0d5 !important;
    border-radius: 12px !important;
    min-height: 52px !important;
    height: 52px !important;
    padding: 0 !important;
    background: #FFFFFF !important;
    margin: 0 !important;
    box-shadow: none !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease !important;
  }

  .lc__otp .opt-input .v-text-field__slot {
    padding: 0 !important;
    margin: 0 !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .lc__otp .opt-input:hover .v-input__slot {
    border-color: #9eb0cc !important;
  }

  .lc__otp .opt-input .v-input__slot::before,
  .lc__otp .opt-input .v-input__slot::after {
    display: none !important;
  }

  .lc__otp .opt-input input {
    font-size: 22px !important;
    font-weight: 700 !important;
    color: #17326b !important;
    text-align: center !important;
    padding: 0 !important;
    margin: 0 !important;
    height: 100% !important;
    width: 100% !important;
    max-height: none !important;
    line-height: 1 !important;
    letter-spacing: 0 !important;
  }

  .lc__otp .opt-input.v-input--is-focused .v-input__slot {
    border-color: #2f6fed !important;
    box-shadow: 0 0 0 4px rgba(47, 111, 237, 0.12) !important;
    outline: none !important;
  }

  /* Focused state */
  .lc__otp .opt-input.v-input--is-focused .v-input__slot {
    border-color: #2f6fed !important;
    background: #FFFFFF !important;
    box-shadow: 0 0 0 4px rgba(47, 111, 237, 0.12) !important;
  }

  .lc__otp .opt-input .v-text-field__details {
    display: none !important;
    min-height: 0 !important;
    height: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .lc__otp .opt-input .v-input__control {
    min-height: 52px !important;
    height: 52px !important;
  }

  .lc__otp .row.pl-4 {
    justify-content: center !important;
    padding: 0 !important;
    margin: 0 !important;
    gap: 0 !important;
    flex-wrap: nowrap !important;
  }

  @media (max-width: 560px) {
    .lc__otp .opt-input,
    .lc__otp .opt-input .v-input__slot,
    .lc__otp .opt-input .v-input__control {
      width: 48px !important;
      min-width: 48px !important;
      height: 48px !important;
      min-height: 48px !important;
      border-radius: 12px !important;
    }
    .lc__otp .opt-input {
      margin: 0 3px !important;
    }
    .lc__otp .opt-input input {
      font-size: 20px !important;
    }
  }

  @media (max-width: 380px) {
    .lc {
      padding: 24px 20px;
    }
    .lc__otp .opt-input,
    .lc__otp .opt-input .v-input__slot,
    .lc__otp .opt-input .v-input__control {
      width: 40px !important;
      min-width: 40px !important;
      height: 40px !important;
      min-height: 40px !important;
    }
    .lc__otp .opt-input input {
      font-size: 18px !important;
    }
  }
</style>

<style scoped>
  /* estilo para pregunta */
  .azul {
    color: #000000;
    font-size: 16px;
    font-weight: 400;
    letter-spacing: -0.31px;
    margin-right: 11px;
  }

  /* estilo para h1 */
  .azul-header {
    color: #041332;
    font-size: 25px;
    font-weight: 700;
    line-height: 34px;
  }

  /* estilo para link ContactUs*/
  .azul-contact {
    font-size: 15px;
    font-weight: 700;
  }

  /* estilo para texto debajo de login */
  .v-application .py-2 {
    color: #878787;
    font-size: 15px;
    font-weight: 500;
  }

  .btn-t {
    width: 155px;
    height: 48px;
    color: white;
    box-shadow: 0 5px 18px rgba(51, 51, 51, 0.13);
    border-radius: 24px;
    margin: 0 auto;
    margin-top: 50px;
  }

  .ui-label {
    color: #061d47; /*!important;*/
    font-size: 20px !important;
    font-weight: bold !important;
  }

  .padd-log {
    padding: 0 73px;
  }

  .card-theme {
    -webkit-box-shadow: 0 20px 15px -15px rgba(0, 0, 0, 0.1) !important;
    -moz-box-shadow: 0 20px 15px -15px rgba(0, 0, 0, 0.1) !important;
    box-shadow: 0 20px 15px -15px rgba(0, 0, 0, 0.1) !important;
  }

  .title_login_custom {
    color: #041332;
    font-size: 25px;
    font-weight: 700;
    line-height: 34px;
  }

  .v-input__control {
    width: 34px;
  }

  .login_conten {
    max-width: 724px !important;
    margin: 0 auto !important;
  }

  @media (max-width: 990px) {
    .login_conten {
      width: 100%;
      height: auto;
    }
  }

  @media (max-width: 768px) {
    .padd-log {
      padding: 0;
    }
  }
</style>


<style scoped>
  /* estilo para h1 */
  .azul-header {
    color: #041332;
    font-size: 25px;
    font-weight: 700;
    line-height: 34px;
  }

  .btn-t {
    width: 155px;
    height: 48px;
    color: white;
    box-shadow: 0 5px 18px rgba(51, 51, 51, 0.13);
    border-radius: 24px;
    margin: 0 auto;
  }

  .ui-label {
    font-size: 20px !important;
    font-weight: bold !important;
    color: #061d47 !important;
    margin-top: 30px !important;
    letter-spacing: normal !important;
    margin-bottom: 20px;
  }

  .tesx--black {
    color: #878787 !important;
    font-size: 15px !important;
    font-weight: 500 !important;
    line-height: 20px !important;
  }

  .padd-log {
    padding: 0 70px;
  }

  .card-theme {
    -webkit-box-shadow: 0 20px 15px -15px rgba(0, 0, 0, 0.1) !important;
    -moz-box-shadow: 0 20px 15px -15px rgba(0, 0, 0, 0.1) !important;
    box-shadow: 0 20px 15px -15px rgba(0, 0, 0, 0.1) !important;
  }

  .btn-t2 {
    box-shadow: none !important;
    text-decoration: underline !important;
    text-transform: capitalize;
    font-size: 15px;
    font-weight: 700;
  }

  .v-text-field-input {
    margin: 4px 0 4px;
  }

  .input-inner-space input#input32 {
    margin-top: 5px;
    max-height: 36px;
  }

  .input-inner-space input#input-32,
  .input-inner-space input#input-35 {
    letter-spacing: 8px !important;
    font-size: 18px;
    font-weight: 400;
    margin-top: 12px !important;
    margin-bottom: -1px;
  }

  .input-inner-space input {
    padding: 16px 0px 8px !important;
  }

  .v-input__slot {
    margin-top: 5px !important;
    padding-top: 5px !important;
    margin-bottom: 8px !important;
  }

  label.msg {
    width: 474px;
    color: #061d47;
    font-size: 16px;
    font-weight: 700;
    line-height: 14px;
  }

  /* authorization digit */

  .title_login_custom {
    color: #041332;
    font-size: 25px;
    font-weight: 700;
    line-height: 34px;
  }

  .v-text-field__slot input {
    padding-top: 15px;
    letter-spacing: 8px;
  }

  .login_conten {
    max-width: 724px !important;
    margin: 0 auto !important;
  }

  @media (max-width: 990px) {
    .login_conten {
      width: 100%;
      height: auto;
    }
  }

  @media (max-width: 1100px) {
    .padd-log {
      padding: 0 40px;
    }
  }

  @media (max-width: 768px) {
    .azul {
      margin-left: 0px !important;
    }

    .padd-log {
      padding: 0 20px;
    }
  }

  @media (max-width: 480px) {
    .padd-log {
      padding: 0;
    }

    .login_conten {
      max-width: 100% !important;
    }
  }

  .bg_body {
    padding-top: 20px;
  }

  /* Login page background — premium subtle vertical gradient. Anchor the
     card to the top of the viewport instead of centering vertically so a
     short viewport (tablet, landscape phone, Chrome devtools window)
     can't clip the card's header above the visible area. */
  .login .bg_body {
    background: linear-gradient(180deg, #f7f8fb 0%, #f3f5f9 100%) !important;
    min-height: calc(100vh - 126px);
    display: flex !important;
    align-items: flex-start !important;
    justify-content: center !important;
    padding-top: 48px !important;
    padding-bottom: 40px !important;
  }

  /* On tall desktops, allow gentle vertical centering — but only when the
     viewport is actually tall enough to fit the card AND the header. */
  @media (min-height: 900px) {
    .login .bg_body {
      align-items: center !important;
      padding-top: 0 !important;
    }
  }

  /* Mobile — center the card vertically so the empty space below isn't
     huge on a full-height portrait phone. Short viewports (landscape,
     keyboard-open) fall back to flex-start below so the card header
     never sits above the visible area. */
  @media (max-width: 768px) {
    .login .bg_body {
      min-height: calc(100vh - 88px);
      align-items: center !important;
      padding-top: 20px !important;
      padding-bottom: 24px !important;
    }
  }

  @media (max-width: 420px) {
    .login .bg_body {
      min-height: calc(100vh - 68px);
      padding-top: 14px !important;
      padding-bottom: 20px !important;
    }
  }

  /* Short-viewport fallback — once the viewport is too short for the
     card to fit comfortably when centered, top-align so the header
     stays in view. */
  @media (max-width: 768px) and (max-height: 640px) {
    .login .bg_body {
      align-items: flex-start !important;
    }
  }

  .login .bg_body > .row {
    width: 100%;
    margin: 0 !important;
  }

  /* ── New login card ──────────────────────────────── */
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  .lc {
    --kiey-navy: #0C2454;
    --kiey-blue: #2F6FED;
    --kiey-blue-dark: #255ED3;
    --kiey-blue-soft: #F5F9FF;
    --kiey-blue-border: #AFC2EB;

    --surface-panel: #FFFFFF;
    --surface-soft: #F8FAFC;

    --text-primary: #0C2454;
    --text-secondary: #5E6B85;
    --text-muted: #98A2B3;
    --text-link: #2F6FED;

    --border-default: #D9E1F0;
    --border-soft: #E6ECF5;
    --border-strong: #C7D2E0;

    --shadow-modal: 0 20px 60px rgba(8, 30, 74, 0.14);
    --shadow-button: 0 4px 12px rgba(47, 111, 237, 0.20);

    --radius-modal: 20px;
    --radius-input: 10px;
    --radius-button: 10px;

    width: 520px;
    max-width: calc(100vw - 32px);
    margin: 0 auto;
    background: var(--surface-panel);
    border: 1px solid #e5eaf3;
    border-radius: 24px;
    padding: 36px 48px 32px;
    box-shadow: 0 26px 60px rgba(28, 51, 102, 0.10), 0 10px 24px rgba(28, 51, 102, 0.06);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  }

  .lc__title {
    font-weight: 700;
    font-size: 24px;
    line-height: 1.2;
    color: var(--text-primary);
    text-align: center;
    margin: 0 0 8px;
  }

  .lc__subtitle {
    font-size: 15px;
    line-height: 1.45;
    color: var(--text-secondary);
    text-align: center;
    margin: 0 auto 16px;
    max-width: 360px;
  }

  .lc__field {
    max-width: 320px;
    margin: 0 auto 10px;
  }

  .lc__input {
    width: 100%;
    max-width: 320px;
    display: block;
    margin: 0 auto;
    height: 44px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-input);
    padding: 0 14px;
    font-size: 15px;
    text-align: center;
    color: var(--text-primary);
    outline: none;
    transition: border-color 160ms ease, box-shadow 160ms ease;
    box-sizing: border-box;
    font-family: inherit;
    background: var(--surface-panel);
  }

  .lc__input::placeholder { color: var(--text-muted); }
  .lc__input:hover { border-color: var(--border-strong); }

  .lc__input:focus {
    border-color: var(--kiey-blue);
    box-shadow: 0 0 0 3px rgba(47, 111, 237, 0.12);
  }

  .lc__input--error { border-color: #DC3545; }

  .lc__error {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    color: #DC3545;
    text-align: center;
  }

  .lc__links {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--text-secondary);
    max-width: 320px;
    margin: 10px auto 0;
    position: relative;
    z-index: 1;
  }

  .lc__link { color: var(--text-secondary); text-decoration: none; }
  .lc__link:hover { text-decoration: underline; color: var(--text-primary); }
  .lc__link--blue { color: var(--text-link); text-decoration: none; font-weight: 600; }
  .lc__link--blue:hover { text-decoration: underline; }
  .lc__dot { color: #C0C7D4; }

  .lc__helper {
    font-size: 13px;
    line-height: 1.4;
    color: var(--text-muted);
    text-align: center;
    margin: 10px auto 0;
    max-width: 360px;
  }

  .lc__cta {
    width: 100%;
    max-width: 360px;
    height: 44px;
    border-radius: var(--radius-button);
    background: linear-gradient(180deg, #3d74ea 0%, #2f6fed 100%);
    color: #FFFFFF;
    font-size: 15px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    margin: 14px auto 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 10px 20px rgba(47, 111, 237, 0.16), 0 4px 10px rgba(47, 111, 237, 0.10);
    transition: filter 160ms ease, transform 120ms ease, box-shadow 160ms ease;
    font-family: inherit;
  }

  .lc__cta:hover:not(:disabled) {
    background: linear-gradient(180deg, #3d74ea 0%, #2f6fed 100%);
    filter: brightness(1.03);
    box-shadow: 0 14px 26px rgba(47, 111, 237, 0.22), 0 6px 14px rgba(47, 111, 237, 0.12);
  }
  .lc__cta:active:not(:disabled) { transform: translateY(1px); }
  .lc__cta:disabled { background: #C7D2FE; cursor: not-allowed; box-shadow: none; }

  .lc__trust {
    font-size: 12px;
    line-height: 1.4;
    color: var(--text-muted);
    text-align: center;
    margin: 10px 0 0;
  }

  .lc__change {
    font-size: 14px;
    color: var(--text-secondary);
    text-align: center;
    margin: 0 0 12px;
  }

  .lc__otp {
    margin: 12px 0 10px;
  }

  .lc__resend {
    text-align: center;
    margin: 0 0 12px;
    font-size: 13px;
    color: var(--text-secondary);
  }

  .lc .v-form,
  .lc form.v-form {
    width: 100%;
    display: block;
  }

  /* ── OTP screen overrides (step 2) ──────────────────── */
  .lc--otp {
    max-width: 520px;
    width: 100%;
    padding: 36px 48px 32px;
    border-radius: 24px;
    border: 1px solid #e5eaf3;
    background: #FFFFFF;
    box-shadow: 0 26px 60px rgba(28, 51, 102, 0.10), 0 10px 24px rgba(28, 51, 102, 0.06);
  }

  .lc--otp .lc__title {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.02em;
    text-align: center;
    color: #17326b;
    margin: 0 0 8px;
  }

  .lc--otp .lc__subtitle {
    font-size: 14px;
    line-height: 1.4;
    font-weight: 500;
    text-align: center;
    color: #617292;
    margin: 0 0 6px;
    max-width: none;
  }

  .lc--otp .lc__subtitle strong {
    color: #17326b;
    font-weight: 700;
  }

  .lc--otp .lc__change {
    font-size: 13px;
    line-height: 1.35;
    color: #617292;
    margin: 0 0 16px;
  }

  .lc--otp .lc__resend {
    font-size: 13px;
    line-height: 1.35;
    color: #617292;
    margin: 14px 0 20px;
  }

  .lc--otp .lc__change a,
  .lc--otp .lc__resend a,
  .lc--otp .lc__link--blue {
    color: #2f6fed;
    font-weight: 700;
    text-decoration: none;
  }

  .lc--otp .lc__change a:hover,
  .lc--otp .lc__resend a:hover,
  .lc--otp .lc__link--blue:hover {
    text-decoration: underline;
  }

  .lc--otp .lc__otp {
    margin: 0;
    padding: 0;
  }

  .lc--otp .lc__cta {
    height: 60px;
    max-width: 100%;
    border-radius: 16px;
    border: none;
    background: linear-gradient(180deg, #3d74ea 0%, #2f6fed 100%);
    color: #FFFFFF;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.01em;
    box-shadow: 0 10px 20px rgba(47, 111, 237, 0.16), 0 4px 10px rgba(47, 111, 237, 0.10);
    transition: transform 180ms ease, box-shadow 180ms ease, filter 180ms ease;
    margin: 0;
  }

  .lc--otp .lc__cta:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.03);
    box-shadow: 0 16px 30px rgba(47, 111, 237, 0.22), 0 6px 14px rgba(47, 111, 237, 0.12);
    background: linear-gradient(180deg, #3d74ea 0%, #2f6fed 100%);
  }

  .lc--otp .lc__cta:active:not(:disabled) {
    transform: translateY(0);
  }

  .lc--otp .lc__cta:disabled {
    opacity: 0.55;
    box-shadow: none;
    cursor: not-allowed;
    background: linear-gradient(180deg, #3d74ea 0%, #2f6fed 100%);
  }

  .lc--otp .lc__trust {
    margin-top: 12px;
    font-size: 14px;
    line-height: 1.35;
    font-weight: 500;
    color: #98a4b8;
    text-align: center;
  }

  @media (max-width: 560px) {
    .lc {
      padding: 28px 24px 24px;
      border-radius: 20px;
    }
    .lc__title { font-size: 22px; }
    .lc__subtitle { font-size: 14px; }
    .lc--otp {
      padding: 28px 24px 24px;
      border-radius: 20px;
    }
    .lc--otp .lc__title { font-size: 22px; }
    .lc--otp .lc__cta { height: 60px; font-size: 16px; border-radius: 16px; }
  }

  @media (max-width: 380px) {
    .lc {
      padding: 24px 18px 20px;
    }
    .lc__title { font-size: 20px; }
    .lc__subtitle { font-size: 13px; }
  }
</style>
